import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Database, PlugsConnected, Broadcast, CheckCircle, XCircle, CircleNotch,
  Stack, ShieldCheck, ArrowLeft, Cube,
} from "@phosphor-icons/react";

const StatusDot = ({ ok }) =>
  ok ? (
    <CheckCircle size={18} weight="fill" className="text-emerald-400" />
  ) : (
    <XCircle size={18} weight="fill" className="text-red-400" />
  );

export default function SystemStatus() {
  const navigate = useNavigate();
  const [preflight, setPreflight] = useState(null);
  const [summary, setSummary] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, m] = await Promise.all([
          api.get("/preflight"),
          api.get("/meta/summary"),
          api.get("/modules"),
        ]);
        setPreflight(p.data);
        setSummary(s.data);
        setModules(m.data.modules);
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B1220] text-zinc-100 font-mono relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover" }}
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <button
          data-testid="status-back-link"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-100 transition-colors mb-8"
        >
          <ArrowLeft size={14} weight="bold" /> back to platform
        </button>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={22} weight="fill" className="text-[#F7A81B]" />
          <h1 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl text-white font-sans">
            Pre-flight & Registry
          </h1>
        </div>
        <p className="text-zinc-500 text-xs md:text-sm mb-10">
          rotary-platform-core · stage-1 · {summary?.district?.name || "District 3192"} · {summary?.rotary_year?.label || "RY 2026-27"}
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <CircleNotch size={18} className="animate-spin" weight="bold" /> running gates…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Gates */}
            <div className="grid md:grid-cols-2 gap-4">
              <div data-testid="gate-mongo" className="border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Database size={18} weight="duotone" className="text-[#5B8DEF]" />
                    <span className="text-sm">MongoDB ping</span>
                  </div>
                  <StatusDot ok={preflight?.mongo?.status === "ok"} />
                </div>
                <div className="mt-3 text-xs text-zinc-500 space-y-1">
                  <p>db: <span className="text-zinc-300">{preflight?.mongo?.database}</span></p>
                  <p>latency: <span className="text-[#F7A81B]">{preflight?.mongo?.latency_ms} ms</span></p>
                </div>
              </div>

              <div data-testid="gate-auth" className="border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <PlugsConnected size={18} weight="duotone" className="text-[#5B8DEF]" />
                    <span className="text-sm">Authentication gate</span>
                  </div>
                  <StatusDot ok={!!preflight?.auth?.email_delivery_configured} />
                </div>
                <div className="mt-3 text-xs text-zinc-500 space-y-1">
                  <p>provider: <span className="text-zinc-300">{preflight?.auth?.provider}</span></p>
                  <p>flow: <span className="text-zinc-300">{preflight?.auth?.flow}</span></p>
                  <p>email delivery: <span className={preflight?.auth?.email_delivery_configured ? "text-emerald-400" : "text-[#F7A81B]"}>
                    {String(preflight?.auth?.email_delivery_configured)}
                  </span></p>
                </div>
              </div>
            </div>

            {/* Module registry */}
            <div className="border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 text-zinc-300">
                <Stack size={18} weight="duotone" className="text-[#5B8DEF]" />
                <span className="text-sm">Module registry</span>
              </div>
              <div data-testid="module-registry" className="divide-y divide-white/5">
                {modules.map((m) => (
                  <div key={m.module_id} className="flex items-center justify-between px-5 py-3 text-xs">
                    <div className="flex items-center gap-3">
                      <span className={`h-2 w-2 ${m.enabled ? "bg-emerald-400" : "bg-zinc-600"}`} />
                      <span className="text-zinc-200">{m.module_id}</span>
                      <span className="text-zinc-600">{m.route_prefix}</span>
                    </div>
                    <span className={`uppercase tracking-wider ${
                      m.status === "hidden" ? "text-zinc-500" : "text-[#F7A81B]"
                    }`}>
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Collection counts */}
            <div className="border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 text-zinc-300">
                <Cube size={18} weight="duotone" className="text-[#5B8DEF]" />
                <span className="text-sm">Core collections · {summary?.core_collections}</span>
              </div>
              <div data-testid="collection-counts" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-white/5">
                {summary && Object.entries(summary.counts).map(([k, v]) => (
                  <div key={k} className="bg-[#0B1220] px-4 py-3">
                    <p className="text-[10px] text-zinc-500 truncate">{k}</p>
                    <p className="text-lg text-white">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[11px] text-zinc-600 flex items-center gap-2">
              <Broadcast size={13} weight="duotone" /> gates_pass: {String(preflight?.gates_pass)} · seed: reconciled fictional records only
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
